import { useCallback, useEffect, useRef } from 'react';

import { getAllFieldsTouched } from '../../../../../common';
import { toDischargeCriteriaFieldPayload } from '../utils';
import { DISCHARGE_CRITERIA_SCHEMA } from '../validationSchema';
import { getChangedDischargeFieldValues, getDischargeValuesWithFieldOverride } from './utils';

import type { DischargeCriteriaFieldName } from '../../../../../../store/treatmentPlanV3';
import type { SaveField } from '../../../../../common';
import type { DischargeCriteriaFormValues, DischargeCriteriaSavedFieldValues } from '../types';
import type { SaveDischargeFieldOnBlurParams } from './types';

export default function useSaveDischargeFieldOnBlur({
  formik,
  initialValues,
  onUpdate,
}: SaveDischargeFieldOnBlurParams) {
  const savedFieldValuesRef = useRef<DischargeCriteriaSavedFieldValues>({ ...initialValues });
  const savingPromiseRef = useRef(Promise.resolve());

  useEffect(() => {
    savedFieldValuesRef.current = { ...initialValues };
  }, [initialValues]);

  const performDischargeCriteriaSave = useCallback(
    async (name: DischargeCriteriaFieldName, values: DischargeCriteriaFormValues) => {
      const currentSave = savingPromiseRef.current
        .catch(() => undefined)
        .then(async () => {
          const changedValue = getChangedDischargeFieldValues(values, savedFieldValuesRef.current)[
            name
          ];
          if (changedValue === undefined) {
            return;
          }

          const didUpdate = await onUpdate(toDischargeCriteriaFieldPayload(name, values));
          if (!didUpdate) {
            return;
          }

          await formik.setTouched(getAllFieldsTouched(values), true);
          savedFieldValuesRef.current = { ...savedFieldValuesRef.current, [name]: changedValue };
        });

      savingPromiseRef.current = currentSave;
      await currentSave;
    },
    [formik, onUpdate],
  );

  const saveField: SaveField<DischargeCriteriaFormValues> = useCallback(
    async (name, value) => {
      const values = getDischargeValuesWithFieldOverride(formik.values, name, value);

      try {
        await DISCHARGE_CRITERIA_SCHEMA.validateAt(name, values);
      } catch {
        return;
      }

      await performDischargeCriteriaSave(name, values);
    },
    [formik, performDischargeCriteriaSave],
  );

  return { saveField };
}
